import type { Session } from '@cybersailor/slauth-ts'
import { getDefaultClient } from '@arcubase/client-ts'
import {
  clearSlauthSession,
  getSlauthSession,
  refreshSlauthSession,
  restoreSlauthSession,
  setSlauthRuntimeHandlers,
} from './slauth'

let currentSession: Session | null = null

function syncSession(session: Session | null | undefined): Session | null {
  currentSession = session || null
  getDefaultClient().setToken(currentSession?.access_token || null)
  return currentSession
}

async function resetApplicationAuthState() {
  const { useUserStore } = await import('@/store/user')
  const store = useUserStore()
  await store.resetAuthState()
}

export async function getSession(): Promise<Session | null> {
  return syncSession(await getSlauthSession())
}

export async function restoreSession(): Promise<Session | null> {
  return syncSession(await restoreSlauthSession())
}

export async function refreshSession(): Promise<Session | null> {
  return syncSession(await refreshSlauthSession())
}

export async function logout(): Promise<void> {
  try {
    await clearSlauthSession()
    syncSession(null)
  } finally {
    await resetApplicationAuthState()
  }
}

export async function hasSession(): Promise<boolean> {
  return !!(await getSession())?.access_token
}

export function hasCachedSession(): boolean {
  return !!currentSession?.access_token
}

export function syncTenant(tenantId: string | number | null | undefined): void {
  getDefaultClient().setTenant(tenantId ? String(tenantId) : null)
}

export async function getAccessToken(): Promise<string> {
  return (await getSession())?.access_token || ''
}

export async function getAuthHeaders(
  tenantId?: string | number | null,
  extraHeaders: Record<string, string> = {},
): Promise<Record<string, string>> {
  const token = await getAccessToken()
  const headers: Record<string, string> = { ...extraHeaders }
  if (token) headers.Authorization = `Bearer ${token}`
  if (tenantId != null && tenantId !== '') headers['X-Tenant-Id'] = String(tenantId)
  return headers
}

export async function createAuthenticatedEventSource(
  url: string,
  options: {
    tenantId?: string | number | null
    headers?: Record<string, string>
    heartbeatTimeout?: number
  } = {},
) {
  const { EventSourcePolyfill } = await import('event-source-polyfill')
  return new EventSourcePolyfill(url, {
    headers: await getAuthHeaders(options.tenantId, options.headers || {}),
    heartbeatTimeout: options.heartbeatTimeout,
  })
}

setSlauthRuntimeHandlers({
  onSessionRefreshed: syncSession,
  onUnauthorized: logout,
})
